import {
  RUTERO_BOOKING_LIST_CREATED_BY_PATTERN,
  RUTERO_BOOKING_LIST_LIMITS,
} from '@bandido/shared/types';

/**
 * EL ÁMBITO del listado: de quién son las reservas que se están mirando.
 *
 * No es un filtro más — es un SELECTOR que condiciona todo lo que la pantalla
 * dice, contadores de pestañas incluidos: en "Mías" el badge cuenta MIS
 * pendientes, no los de la agencia. Por eso vive acá y no entre los filtros
 * facetados, y por eso viaja en la query key (§21.2): servir la cache de otro
 * ámbito le mostraría al vendedor la cola del operador.
 *
 * Del lado del contrato es un `createdBy` (`ListRuteroBookingsDto`), que NUNCA
 * amplía nada: la agencia sale del JWT del otro lado (§21.1).
 */
export type BookingListScopeKind = 'mine' | 'all' | 'advisor';

/** El asesor elegido: el id viaja, el rótulo solo se dibuja. */
export interface BookingListAdvisor {
  id: string;
  label: string;
}

export interface BookingListScope {
  kind: BookingListScopeKind;
  /** Presente SOLO con `advisor`. */
  advisor?: BookingListAdvisor;
}

export const MINE_BOOKING_LIST_SCOPE: BookingListScope = { kind: 'mine' };
export const ALL_BOOKING_LIST_SCOPE: BookingListScope = { kind: 'all' };

/**
 * Un `createdBy` que el endpoint va a aceptar. Se valida con el MISMO patrón y
 * el MISMO tope que el DTO del borde (§21.22): un id inválido no debe generar
 * una consulta que el servidor rechazaría con 400.
 */
export function isBookingListAdvisorId(id: unknown): id is string {
  return (
    typeof id === 'string' &&
    id.length <= RUTERO_BOOKING_LIST_LIMITS.createdByMaxLength &&
    RUTERO_BOOKING_LIST_CREATED_BY_PATTERN.test(id)
  );
}

/**
 * Qué `createdBy` mandar, o que todavía no se puede pedir nada.
 *
 * Los tres estados son distintos y no se colapsan en `undefined` (§21.18.4):
 * "sin createdBy" ES el ámbito de toda la agencia, así que un "Mías" cuyo
 * usuario todavía no cargó no puede degradar a eso — mostraría las reservas de
 * todos con el selector diciendo "Mías". Mientras no se resuelve, la pantalla
 * no consulta.
 */
export type BookingListScopeResolution =
  | { status: 'ready'; createdBy?: string }
  | { status: 'unresolved' };

export function resolveBookingListScope(
  scope: BookingListScope,
  currentUserId?: string,
): BookingListScopeResolution {
  if (scope.kind === 'all') return { status: 'ready' };
  if (scope.kind === 'advisor') {
    return isBookingListAdvisorId(scope.advisor?.id)
      ? { status: 'ready', createdBy: scope.advisor?.id }
      : { status: 'unresolved' };
  }
  return isBookingListAdvisorId(currentUserId)
    ? { status: 'ready', createdBy: currentUserId }
    : { status: 'unresolved' };
}
